/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';
import JSON5 from 'json5';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  FeedbackCommentProps,
  FeedbackSectionId,
  UserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { parseImpactDetailsAnswer } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactDetails';
import {
  IndicatorSectionKey,
  IndicatorWithUpdate,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { SuccessResponse } from '../../ReportsBeta/NewReport/api';
import { ApolloClient, FetchResult } from 'apollo-boost';
import {
  getTimePeriodText,
  IndicatorDetails,
  SectionIndicators,
  sectionKeyToString,
} from '../../ReportBuilder/Components/IndicatorDetails/utils';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { TrueImpactIndicatorFilesRender } from 'shared/design-system/components/TrueImpactFileRender';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { parseImpactSummaryAnswer } from '../../NPOFeedback/FeedbackDetails/ImpactSummaryDetails';
import { IMPACT_SUMMARY_QUESTION_ID } from '../../NPOFeedback/utils';
import ImpactDetailCommentGroup from './ImpactDetailCommentGroup';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { INDICATOR_SUMMARY_QUESTION_ID } from '../../NPOFeedback/utils';
import { useFeedback } from '../FeedbackProvider';

const RenderIndicator: React.FC<{
  indicator:
    | { indicator: IndicatorDetails; summary: IndicatorWithUpdate }
    | undefined;
  sectionKey: IndicatorSectionKey;
  reportId: number | string;
  reportStartDate: string;
  reportEndDate: string;
}> = ({ indicator, sectionKey, reportId, reportStartDate, reportEndDate }) => {
  const { getSourcesAndAssumptions } = useIndicator();

  if (!indicator) return <>This indicator was removed</>;

  const description = getSourcesAndAssumptions(indicator.indicator);
  const i = indicator.indicator;
  const summary = indicator.summary;
  return (
    <>
      <ul>
        <li>Stage: {sectionKeyToString(sectionKey)}</li>
        <li>Forecast: {numberWithCommas(i.forecast)}</li>
        {i.finalForecast && (
          <li>Final Forecast: {numberWithCommas(i.finalForecast)}</li>
        )}
        {i.timePeriod && (
          <li>
            Impact horizon:{' '}
            {getTimePeriodText(i.timePeriod, reportStartDate, reportEndDate)}
          </li>
        )}
        {description && <li>Sources and assumptions: {description}</li>}
        <li>Measurement: {i.measurement}</li>
        <li>
          Description: {summary.updatedText || 'No description was provided'}
        </li>
      </ul>
      <TrueImpactIndicatorFilesRender indicator={i} />
    </>
  );
};

const RenderSection: React.FC<{
  sectionKey: IndicatorSectionKey;
  indicators: IndicatorDetails[] | undefined;
}> = ({ indicators }) => {
  if (!indicators || indicators.length === 0)
    return <>This section was removed</>;
  return (
    <ol>
      {indicators.map(indicator => (
        <li key={indicator.indicatorId}>
          {indicator.indicatorName}
          {/* <RenderIndicator indicator={indicator} sectionKey={sectionKey} /> */}
        </li>
      ))}
    </ol>
  );
};

export const impactDetailsCommentsGroup = (
  impactDetails: SectionIndicators | undefined,
  commentsList: FeedbackCommentProps[],
): GroupedComments => {
  const grouped: GroupedComments = {
    overall: [],
    leftOver: [],
    sections: {} as Record<IndicatorSectionKey, FeedbackCommentProps[]>,
    indicators: {} as Record<
      IndicatorSectionKey,
      Record<number, FeedbackCommentProps[]>
    >,
  };

  if (!impactDetails) {
    return {
      ...grouped,
      overall: [...commentsList],
    };
  }

  (Object.keys(impactDetails) as IndicatorSectionKey[]).forEach(sectionKey => {
    grouped.sections[sectionKey] = [];
    grouped.indicators[sectionKey] = {};

    impactDetails[sectionKey]?.forEach(indicator => {
      grouped.indicators[sectionKey][indicator.indicatorId] = [];
    });
  });

  commentsList.forEach(comment => {
    if (!comment.details) {
      grouped.overall.push(comment);
      return;
    }
    try {
      const details = JSON5.parse(comment.details) as {
        sectionId?: IndicatorSectionKey;
        sectionKey?: IndicatorSectionKey;
        indicatorId?: number;
      };
      const sectionKey = details.sectionId || details.sectionKey;
      let commentAdded = false;

      if (!sectionKey) {
        grouped.overall.push(comment);
        commentAdded = true;
      } else if (details.indicatorId === undefined) {
        if (grouped.sections[sectionKey]) {
          grouped.sections[sectionKey].push(comment);
          commentAdded = true;
        }
      } else {
        if (
          grouped.indicators[sectionKey]?.[details.indicatorId] !== undefined
        ) {
          grouped.indicators[sectionKey][details.indicatorId].push(comment);
          commentAdded = true;
        }
      }

      // If the comment wasn't added to any group, add it to leftOver
      if (!commentAdded) {
        grouped.leftOver.push(comment);
      }
    } catch (e) {
      console.error('Error parsing comment details:', e);
      grouped.overall.push(comment);
    }
  });
  return grouped;
};

interface ImpactDetailRenderProps {
  answer: Answer;
  answersById: {
    [id: number]: Answer;
  };
  reportStartDate: string;
  reportEndDate: string;
  isFeedbackVerification: boolean;
  comments: FeedbackCommentProps[];
  openEditModal: () => unknown;
  reportId: number;
  currentUserId?: string;
  currentUserName: string;
  currentUserEmail: string;
  currentUserType: UserType;
  handleDelete: (
    client: ApolloClient<object>,
    commentId: number,
    comment: string,
    reportId: number,
    sectionId: FeedbackSectionId,
  ) => Promise<
    FetchResult<
      SuccessResponse<'deleteComment'>,
      Record<string, any>,
      Record<string, any>
    >
  >;
  handleSave: (
    client: ApolloClient<object>,
    commentId: number,
    comment: string,
    reportId: number,
    sectionId: FeedbackSectionId,
    details?: string,
  ) => Promise<
    FetchResult<
      SuccessResponse<'updateComment'>,
      Record<string, any>,
      Record<string, any>
    >
  >;
  onSuccessSave: (comment: FeedbackCommentProps, index: number) => unknown;
  onSuccessDelete: (index: number) => unknown;
}

export type GroupedComments = {
  overall: FeedbackCommentProps[];
  leftOver: FeedbackCommentProps[];
  sections: Record<IndicatorSectionKey, FeedbackCommentProps[]>;
  indicators: Record<
    IndicatorSectionKey,
    Record<number, FeedbackCommentProps[]>
  >;
};

const StyledDiv = styled.div`
  .review-text {
    margin-bottom: ${SPACERS.medium};
  }
`;

const ImpactDetailComments: React.FC<ImpactDetailRenderProps> = ({
  answer,
  answersById,
  reportStartDate,
  reportEndDate,
  comments,
  isFeedbackVerification,
  openEditModal,
  reportId,
  currentUserId,
  currentUserEmail,
  currentUserType,
  currentUserName,
  handleDelete,
  handleSave,
  onSuccessSave,
  onSuccessDelete,
}) => {
  const { addOrUpdateCommentState, getLastResolvedAt } = useFeedback();
  const resolvedAt = getLastResolvedAt(INDICATOR_SUMMARY_QUESTION_ID);

  const onSaveSuccess = useCallback(
    (questionId: number, comment: FeedbackCommentProps) => {
      addOrUpdateCommentState(questionId, comment, undefined, false);
    },
    [addOrUpdateCommentState],
  );

  const impactDetails = useMemo(
    () => parseImpactDetailsAnswer(answer?.text || ''),
    [answer?.text],
  );
  const impactSummary = useMemo(
    () =>
      parseImpactSummaryAnswer(
        answersById[IMPACT_SUMMARY_QUESTION_ID]?.text || '',
      ),
    [answersById],
  );

  const groupedComments = useMemo(
    () => impactDetailsCommentsGroup(impactDetails, comments),
    [comments, impactDetails],
  );

  const getIndicator = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      indicatorId: number,
    ):
      | { indicator: IndicatorDetails; summary: IndicatorWithUpdate }
      | undefined => {
      const indicator = impactDetails?.[sectionKey]?.find(
        i => i.indicatorId === indicatorId,
      );
      const summary = impactSummary?.[sectionKey]?.find(
        i => i.id === indicatorId,
      );
      if (!indicator || !summary) return;
      return { indicator, summary };
    },
    [impactDetails, impactSummary],
  );

  const hasAnyComments = useMemo(() => {
    if (groupedComments.overall.length > 0) return true;
    if (Object.values(groupedComments.sections).some(arr => arr.length > 0))
      return true;
    return Object.values(groupedComments.indicators).some(section =>
      Object.values(section).some(comments => comments.length > 0),
    );
  }, [groupedComments]);

  if (!hasAnyComments) {
    return (
      <StyledDiv>
        <TrueImpactText variant="body" className="review-text">
          No feedback comments yet.
        </TrueImpactText>
      </StyledDiv>
    );
  }

  if (!impactSummary || !impactDetails) {
    return (
      <StyledDiv>
        <TrueImpactText variant="body" color="critical" className="review-text">
          No impact model was found. This may be due to the model not being
          saved correctly.
        </TrueImpactText>
      </StyledDiv>
    );
  }

  const props = {
    reportId,
    answer,
    questionId: INDICATOR_SUMMARY_QUESTION_ID,
    resolvedAt,
    currentUserId,
    currentUserName,
    currentUserEmail,
    currentUserType,
    handleDelete,
    handleSave,
    onSuccessSave,
    onSuccessDelete,
    onSaveSuccess,
  };

  return (
    <StyledDiv>
      {isFeedbackVerification ? (
        <>
          <ImpactDetailCommentGroup
            {...props}
            title="Overall Impact Model"
            comments={groupedComments.overall}
          />
          {Object.entries(groupedComments.sections).map(
            ([sectionKey, sectionComments]) => {
              if (sectionComments.length === 0) return null;
              const key = sectionKey as IndicatorSectionKey;
              const title = `Stage: ${sectionKeyToString(key)}`;
              return (
                <ImpactDetailCommentGroup
                  {...props}
                  key={title}
                  title={title}
                  comments={sectionComments}
                  details={JSON.stringify({ sectionId: key })}
                  description={
                    <RenderSection
                      sectionKey={key}
                      indicators={impactDetails?.[key]}
                    />
                  }
                />
              );
            },
          )}
          {Object.entries(groupedComments.indicators).map(
            ([sectionKey, indicators]) => {
              return Object.entries(indicators).map(
                ([indicatorId, indicatorComments]) => {
                  if (indicatorComments.length === 0) return null;
                  const key = sectionKey as IndicatorSectionKey;
                  const indicator = getIndicator(key, Number(indicatorId));
                  const title = `Indicator: ${indicator?.indicator.indicatorName}`;
                  return (
                    <ImpactDetailCommentGroup
                      {...props}
                      key={title}
                      title={title}
                      comments={indicatorComments}
                      details={JSON.stringify({
                        sectionId: key,
                        indicatorId: Number(indicatorId),
                        indicatorName: indicator?.indicator.indicatorName,
                      })}
                      description={
                        <RenderIndicator
                          sectionKey={key}
                          reportId={reportId}
                          indicator={indicator}
                          reportStartDate={reportStartDate}
                          reportEndDate={reportEndDate}
                        />
                      }
                    />
                  );
                },
              );
            },
          )}
          {groupedComments.leftOver.length > 0 && (
            <ImpactDetailCommentGroup
              {...props}
              hideDetails={false}
              key="deleted-indicators"
              title="Comments from Deleted Indicators"
              comments={groupedComments.leftOver}
              description={
                <TrueImpactText variant="body-small" color="grayDark">
                  These comments are from indicators that no longer exist in the
                  current impact model.
                </TrueImpactText>
              }
            />
          )}
        </>
      ) : (
        <>
          <TrueImpactText variant="heading-subheading">
            You have {comments.length}{' '}
            {comments.length === 1 ? 'feedback' : 'feedbacks'} on your impact
            model.
          </TrueImpactText>
          <TrueImpactText className="review-text">
            Click <strong>View Feedback</strong> to review and make any edits to
            your impact model.
          </TrueImpactText>
        </>
      )}
    </StyledDiv>
  );
};

export default ImpactDetailComments;
